// Founder "Elite Commercial Production Readiness" Section 1 (2026-09-07)
// + "Recurring Subscription Lifecycle" (2026-09-07) -- commercial
// entitlement / billing UI. Stripe is NEVER the source of truth for
// what this card shows -- everything here reads GET /billing/status,
// which reflects the canonical `tenants` row that only the verified
// webhook (backend/services/billing.py) ever writes. Checkout/Portal
// redirect to Stripe-hosted pages; we never touch card details
// directly. `effective_state` (not the raw Stripe status) drives the
// headline pill, so billing truth and access truth never contradict
// each other (e.g. Stripe past_due + Founder courtesy override reads
// as active courtesy access, never "suspended").
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, ExternalLink, Loader2, AlertTriangle, CheckCircle2, Clock, XCircle, RefreshCw } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const EFFECTIVE_STATE_META = {
  NONE: { label: "No active subscription", tone: "neutral", Icon: Clock },
  TRIAL: { label: "Trial", tone: "primary", Icon: Clock },
  ACTIVE: { label: "Active", tone: "good", Icon: CheckCircle2 },
  COURTESY_ACCESS: { label: "Courtesy access", tone: "good", Icon: CheckCircle2 },
  GRACE_PERIOD: { label: "Payment action required", tone: "warn", Icon: AlertTriangle },
  SUSPENDED: { label: "Access suspended", tone: "bad", Icon: XCircle },
};
const TONE_CLASS = {
  neutral: "text-[var(--fop-text-2)] border-[var(--fop-border-2)]",
  primary: "text-[var(--fop-primary)] border-[var(--fop-primary)]",
  good: "text-[var(--st-verified,#3ecf8e)] border-[var(--st-verified,#3ecf8e)]",
  warn: "text-[var(--st-warn,#e0a030)] border-[var(--st-warn,#e0a030)]",
  bad: "text-[var(--st-escalated)] border-[var(--st-escalated)]",
};

const OVERRIDE_LABELS = {
  courtesy_free: "Courtesy access",
  discount: "Discounted billing",
  payment_pause: "Billing temporarily paused",
  entitlement_only: "Access authorized",
  manual_enterprise: "Enterprise arrangement",
};

export function BillingCard() {
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busyKey, setBusyKey] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [cadence, setCadence] = useState("monthly");
  const [cancelBusy, setCancelBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [preview, setPreview] = useState(null); // { lookup_key, data } | null
  const [previewLoading, setPreviewLoading] = useState(false);
  const [cancelPendingBusy, setCancelPendingBusy] = useState(false);

  const cancelPendingChange = async () => {
    setCancelPendingBusy(true);
    try {
      await api.delete("/billing/pending_plan_change");
      toast.success("Scheduled plan change cancelled — you'll stay on your current plan.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel the scheduled change");
    } finally {
      setCancelPendingBusy(false);
    }
  };

  const load = async () => {
    const [s, p] = await Promise.all([
      api.get("/billing/status"),
      api.get("/billing/plans"),
    ]);
    setStatus(s.data);
    setPlans(p.data.plans.filter((pl) => pl.monthly_usd));
    if (s.data.billing_interval === "annual") setCadence("annual");
  };

  // Founder Additive Amendment (2026-09-08) -- MUST be declared before
  // the useEffect below, which references it in the `autocheckout`
  // branch. `const` bindings are NOT hoisted (unlike `function`
  // declarations) -- declaring this after the effect put it in the
  // temporal dead zone at the moment the effect's closure ran,
  // throwing "Cannot access 'checkout' before initialization" and
  // crashing the entire Settings page for every freshly-signed-up
  // Starter owner landing on /settings?autocheckout=... straight from
  // /accept-invite (iteration_239 testing-agent finding).
  const checkout = async (lookup_key, trial = false) => {
    setBusyKey(lookup_key);
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key, origin_url: window.location.origin, trial,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusyKey(null);
    }
  };

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      toast.success("Subscription started", { description: "It can take a few seconds for billing status to update." });
      setTimeout(load, 2000);
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("checkout") === "cancelled") {
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("autocheckout")) {
      // Founder Commercial Amendment (2026-09-07) -- a freshly-verified
      // self-service Starter owner lands here straight from
      // /accept-invite. Drop them into Stripe Checkout for the plan
      // they picked at signup automatically, instead of making them
      // find and click "Subscribe" themselves. `trial=1` (Founder
      // Post-Deployment Repair Directive, 2026-09-29) means they chose
      // "Start free trial" at signup -- Checkout still collects a
      // card, but doesn't charge it for 14 days.
      const lookupKey = params.get("autocheckout");
      const wantsTrial = params.get("trial") === "1";
      window.history.replaceState({}, "", window.location.pathname);
      checkout(lookupKey, wantsTrial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!status) return (
    <div className="fop-surface-2 rounded-lg p-5">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-3">Billing</div>
      <Loader2 size={16} className="animate-spin text-[var(--fop-text-3)]" />
    </div>
  );

  const meta = EFFECTIVE_STATE_META[status.effective_state] || EFFECTIVE_STATE_META.NONE;
  const isSubscribed = status.has_active_subscription;

  const changePlan = async (lookup_key) => {
    setBusyKey(lookup_key);
    try {
      const result = await api.post("/billing/change_plan", { lookup_key });
      if (result.data.effective === "immediate") {
        toast.success("Plan updated", { description: "Your new plan is effective now; any difference was prorated." });
      } else {
        toast.success("Plan change scheduled", { description: "Your current plan stays active through this billing period -- the change takes effect at your next renewal, no charge today." });
      }
      setPreview(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change plan");
    } finally {
      setBusyKey(null);
    }
  };

  // Founder Final Closure Section 6: NEVER show a plan-change price
  // without first asking Stripe -- this dialog is populated ENTIRELY
  // from POST /billing/preview_plan_change (server-side, authoritative).
  const openPreview = async (lookup_key) => {
    setPreviewLoading(true);
    try {
      const { data } = await api.post("/billing/preview_plan_change", { lookup_key });
      setPreview({ lookup_key, data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not preview this plan change");
    } finally {
      setPreviewLoading(false);
    }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: `${window.location.origin}/settings`,
      });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
      setPortalBusy(false);
    }
  };

  const doCancel = async () => {
    setCancelBusy(true);
    try {
      await api.post("/billing/cancel");
      toast.success("Cancellation scheduled", { description: "Your access continues through the end of the current billing period." });
      setConfirmCancel(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel subscription");
    } finally {
      setCancelBusy(false);
    }
  };

  const doReactivate = async () => {
    setCancelBusy(true);
    try {
      await api.post("/billing/reactivate");
      toast.success("Subscription reactivated", { description: "Auto-renewal has resumed." });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reactivate subscription");
    } finally {
      setCancelBusy(false);
    }
  };

  const renewalDate = status.current_period_end ? new Date(status.current_period_end) : null;

  return (
    <div className="fop-surface-2 rounded-lg p-5" data-testid="billing-card">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-3">Billing</div>

      {status.manual_entitlement_override ? (
        <div className="text-sm text-[var(--fop-text)] mb-3" data-testid="billing-manual-override">
          Enterprise contract -- billed directly, not through this portal.
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div>
              <span className={`inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wide border rounded-full px-2.5 py-1 ${TONE_CLASS[meta.tone]}`}
                    data-testid="billing-status-pill">
                <meta.Icon size={12} /> {meta.label}
              </span>
              {status.plan_label && (
                <span className="ml-2 text-sm text-[var(--fop-text-2)]">
                  {status.plan_label} plan{status.billing_interval ? ` · ${status.billing_interval}` : ""}
                </span>
              )}
            </div>
            {status.has_stripe_customer && (
              <button onClick={openPortal} disabled={portalBusy} className="btn btn-ghost text-xs" data-testid="billing-manage-btn">
                {portalBusy ? <Loader2 size={12} className="animate-spin" /> : <CreditCard size={12} />}
                Manage billing
              </button>
            )}
          </div>

          {/* Section 3/5: billing cadence + renewal/cancellation truth, kept
              separate from -- and never contradicting -- effective access. */}
          {isSubscribed && renewalDate && (
            <div className="text-xs text-[var(--fop-text-3)] mb-3" data-testid="billing-renewal-line">
              {status.cancel_at_period_end
                ? `Your subscription will remain active through ${renewalDate.toLocaleDateString()} and will not renew.`
                : `Renews ${status.billing_interval === "annual" ? "annually" : "monthly"} on ${renewalDate.toLocaleDateString()}.`}
            </div>
          )}

          {status.active_override ? (
            <div className="text-xs text-[var(--st-verified,#3ecf8e)] mb-3 flex items-center gap-1.5" data-testid="billing-courtesy-override-banner">
              <CheckCircle2 size={12} />
              {OVERRIDE_LABELS[status.active_override.override_type] || "Courtesy access"} active
              {status.active_override.is_permanent
                ? "."
                : ` through ${new Date(status.active_override.expires_at).toLocaleDateString()}.`}
              {" "}Your account remains fully active.
            </div>
          ) : (
            <>
              {status.effective_state === "GRACE_PERIOD" && status.grace_period_ends_at && (
                <div className="text-xs text-[var(--st-warn,#e0a030)] mb-3" data-testid="billing-grace-warning">
                  Your last payment failed. Update your card by{" "}
                  {new Date(status.grace_period_ends_at).toLocaleDateString()} to avoid losing access.
                </div>
              )}
              {status.effective_state === "SUSPENDED" && (
                <div className="text-xs text-[var(--st-escalated)] mb-3" data-testid="billing-blocked-warning">
                  This account cannot create new projects or invite users until billing is resolved.
                </div>
              )}
            </>
          )}

          {/* Section 5/6: a scheduled (deferred) downgrade or annual->monthly
              change already accepted -- customer keeps current tier, no
              surprise mid-cycle change, exact effective date shown. */}
          {status.pending_plan_change_lookup_key && (
            <div className="text-xs text-[var(--fop-primary)] mb-3 flex items-center gap-2 flex-wrap" data-testid="billing-pending-plan-change-banner">
              <span className="flex items-center gap-1.5">
                <Clock size={12} />
                Plan change to {status.pending_plan_change_label || status.pending_plan_change_lookup_key} ({status.pending_plan_change_cadence || "monthly"}) scheduled for{" "}
                {status.pending_plan_change_effective_at ? new Date(status.pending_plan_change_effective_at).toLocaleDateString() : "your next renewal"}.
                You keep your current plan until then.
              </span>
              <button
                onClick={cancelPendingChange}
                disabled={cancelPendingBusy}
                className="underline text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
                data-testid="billing-cancel-pending-change-btn"
              >
                {cancelPendingBusy ? "Cancelling…" : "Cancel this scheduled change"}
              </button>
            </div>
          )}

          {isSubscribed && (
            <div className="mb-3">
              {status.cancel_at_period_end ? (
                <button onClick={doReactivate} disabled={cancelBusy} className="btn btn-ghost text-xs" data-testid="billing-reactivate-btn">
                  {cancelBusy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                  Reactivate subscription
                </button>
              ) : confirmCancel ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[var(--fop-text-2)]">Cancel at the end of this billing period?</span>
                  <button onClick={doCancel} disabled={cancelBusy} className="btn btn-ghost text-xs text-[var(--st-escalated)]" data-testid="billing-cancel-confirm-btn">
                    {cancelBusy ? <Loader2 size={12} className="animate-spin" /> : "Confirm cancel"}
                  </button>
                  <button onClick={() => setConfirmCancel(false)} className="btn btn-ghost text-xs" data-testid="billing-cancel-abort-btn">Keep subscription</button>
                </div>
              ) : (
                <button onClick={() => setConfirmCancel(true)} className="btn btn-ghost text-xs text-[var(--fop-text-3)]" data-testid="billing-cancel-btn">
                  Cancel subscription
                </button>
              )}
            </div>
          )}

          <div className="flex items-center gap-1 mb-2.5" data-testid="billing-cadence-toggle">
            {["monthly", "annual"].map((c) => (
              <button key={c} onClick={() => setCadence(c)}
                      className={`text-[11px] uppercase tracking-wide px-2.5 py-1 rounded-full border ${cadence === c ? "border-[var(--fop-primary)] text-[var(--fop-primary)]" : "border-[var(--fop-border-2)] text-[var(--fop-text-3)]"}`}
                      data-testid={`billing-cadence-${c}`}>
                {c === "annual" ? "Annual" : "Monthly"}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="billing-plan-grid">
            {plans.map((p) => {
              const lookupKey = cadence === "annual" ? p.lookup_key_annual : p.lookup_key_monthly;
              const price = cadence === "annual" ? p.annual_usd : p.monthly_usd;
              const isCurrent = status.plan_code === p.code && isSubscribed && status.billing_interval === cadence;
              // Founder directive (2026-09-07): never assert a specific
              // savings claim ("save 2 months", "17% off", etc.) unless it
              // is mathematically derived from the ACTUAL configured
              // monthly_usd/annual_usd for THIS plan -- no hardcoded copy.
              const annualDiscountPct = (p.monthly_usd && p.annual_usd)
                ? Math.round((1 - p.annual_usd / (p.monthly_usd * 12)) * 100)
                : 0;
              return (
                <div key={p.code} className={`border rounded-md p-3 ${isCurrent ? "border-[var(--fop-primary)]" : "border-[var(--fop-border-2)]"}`}
                     data-testid={`billing-plan-${p.code}`}>
                  <div className="text-sm font-medium text-[var(--fop-text)]">{p.label}</div>
                  <div className="text-lg text-[var(--fop-text)] mt-1">
                    ${price}<span className="text-xs text-[var(--fop-text-3)]">/{cadence === "annual" ? "yr" : "mo"}</span>
                  </div>
                  {cadence === "annual" && annualDiscountPct > 0 && (
                    <div className="text-[11px] text-[var(--st-verified,#3ecf8e)] mt-0.5" data-testid={`billing-plan-${p.code}-annual-savings`}>
                      Save {annualDiscountPct}% vs. monthly
                    </div>
                  )}
                  <div className="text-[11px] text-[var(--fop-text-3)] mt-1 mb-2.5 line-clamp-2">{p.description}</div>
                  {isCurrent ? (
                    <div className="text-xs font-mono uppercase tracking-wide text-[var(--fop-primary)]">Current plan</div>
                  ) : (
                    <button
                      onClick={() => (isSubscribed ? openPreview(lookupKey) : checkout(lookupKey))}
                      disabled={busyKey === lookupKey || previewLoading || !lookupKey}
                      className="btn btn-primary text-xs w-full"
                      data-testid={`billing-subscribe-${p.code}-${cadence}`}
                    >
                      {(busyKey === lookupKey || previewLoading)
                        ? <Loader2 size={12} className="animate-spin" />
                        : <ExternalLink size={12} />}
                      {isSubscribed ? "Change plan" : "Subscribe"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <AlertDialog open={!!preview} onOpenChange={(open) => { if (!open) setPreview(null); }}>
        <AlertDialogContent data-testid="billing-plan-change-preview-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm plan change</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-[var(--fop-text-2)] space-y-2">
                {preview?.data && (
                  <>
                    <div className="flex justify-between"><span>Current plan</span><span className="font-mono">{status.plan_label || "None"}{status.billing_interval ? ` (${status.billing_interval})` : ""}</span></div>
                    <div className="flex justify-between"><span>New plan</span><span className="font-mono" data-testid="billing-preview-new-plan">{preview.data.new_plan_label} ({preview.data.new_plan_cadence})</span></div>
                    <div className="flex justify-between"><span>Effective</span><span className="font-mono" data-testid="billing-preview-effective-date">{preview.data.immediate ? "Immediately" : new Date(preview.data.effective_date).toLocaleDateString()}</span></div>
                    {preview.data.immediate ? (
                      <>
                        {preview.data.credit_applied > 0 && (
                          <div className="flex justify-between"><span>Credit for unused time</span><span className="font-mono text-[var(--st-verified,#3ecf8e)]">-${preview.data.credit_applied.toFixed(2)}</span></div>
                        )}
                        <div className="flex justify-between font-semibold text-[var(--fop-text)]"><span>Amount due now</span><span className="font-mono" data-testid="billing-preview-amount-due-now">${preview.data.amount_due_now.toFixed(2)}</span></div>
                        <div className="flex justify-between"><span>Next recurring amount</span><span className="font-mono">${preview.data.next_recurring_amount.toFixed(2)}</span></div>
                        {preview.data.next_renewal_date && (
                          <div className="flex justify-between"><span>Next renewal date</span><span className="font-mono">{new Date(preview.data.next_renewal_date).toLocaleDateString()}</span></div>
                        )}
                      </>
                    ) : (
                      <div className="text-xs pt-1" data-testid="billing-preview-deferred-note">{preview.data.note}</div>
                    )}
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="billing-plan-change-preview-cancel">Keep current plan</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => preview && changePlan(preview.lookup_key)}
              disabled={busyKey === preview?.lookup_key}
              data-testid="billing-plan-change-preview-confirm"
            >
              {busyKey === preview?.lookup_key ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
              Confirm change
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
